import React, { useState, useEffect, useRef, useCallback } from "react";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import {
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  Pagination,
  Container,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  LinearProgress,
  Tooltip,
  IconButton,
  Collapse,
} from "@mui/material";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import { format } from "date-fns";
import PageTitle from "../../widgets/PageTitle";
import { LoadingButton } from "@mui/lab";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CloseIcon from "@mui/icons-material/Close";
import TitleActionsContainer from "../../widgets/TitleActionsContainer";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";

// ── Constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 4_000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

// ── Export status helpers ─────────────────────────────────────────────────────

function statusMeta(status) {
  switch (status) {
    case "pending":
      return { label: "Queued — preparing your export…", severity: "info" };
    case "processing":
      return {
        label: "Processing — generating full CSV file…",
        severity: "info",
      };
    case "done":
      return { label: "Export ready!", severity: "success" };
    case "failed":
      return { label: "Export failed. Please try again.", severity: "error" };
    case "cancelled":
      return { label: "Export cancelled.", severity: "warning" };
    default:
      return { label: "Unknown status", severity: "info" };
  }
}

// ── ExportStatusBanner ────────────────────────────────────────────────────────

export function ExportStatusBanner({ jobState, onDismiss, onDownload }) {
  if (!jobState) return null;

  const { status, fileUrl, totalRecords, exportedRecords, error } = jobState;
  const { label, severity } = statusMeta(status);
  const isInProgress = status === "pending" || status === "processing";

  return (
    <Collapse in={!!jobState}>
      <Box sx={{ mt: 1, mb: 1 }}>
        <Alert
          severity={severity}
          icon={
            isInProgress ? (
              <HourglassTopIcon fontSize="inherit" />
            ) : status === "done" ? (
              <DownloadDoneIcon fontSize="inherit" />
            ) : undefined
          }
          action={
            <Stack direction="row" alignItems="center" spacing={1}>
              {status === "done" && fileUrl && (
                <Chip
                  label="Download"
                  size="small"
                  color="success"
                  clickable
                  icon={<DownloadDoneIcon />}
                  onClick={() => onDownload(fileUrl)}
                />
              )}
              <IconButton size="small" onClick={onDismiss} color="inherit">
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Stack>
          }
        >
          <Typography variant="body2" fontWeight={500}>
            {label}
          </Typography>

          {/* Full Export Messaging */}
          {isInProgress && totalRecords != null && (
            <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
              Exporting all {totalRecords.toLocaleString()} records...
            </Typography>
          )}

          {status === "done" && exportedRecords != null && (
            <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
              Successfully exported all {exportedRecords.toLocaleString()}{" "}
              records.
            </Typography>
          )}

          {status === "failed" && error && (
            <Typography
              variant="caption"
              display="block"
              color="error"
              sx={{ mt: 0.25 }}
            >
              {error}
            </Typography>
          )}
        </Alert>

        {isInProgress && (
          <LinearProgress
            variant="indeterminate"
            color={severity === "info" ? "primary" : "success"}
            sx={{ borderRadius: "0 0 4px 4px" }}
          />
        )}
      </Box>
    </Collapse>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const TransactionHistory = () => {
  const [page, setPage] = useState(1);
  const [usageType, setUsageType] = useState("");

  const [requesting, setRequesting] = useState(false);
  const [exportJob, setExportJob] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const pollTimerRef = useRef(null);
  const pollStartRef = useRef(null);

  const { data, isLoading, isError, error } = useApiEp(
    `/user/my-transactions?page=${page}&type=${usageType}`
  );

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollJobStatus = useCallback(
    async (jobId) => {
      if (Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
        stopPolling();
        setExportJob((prev) => ({
          ...prev,
          status: "failed",
          error: "Export timed out. Please try again.",
        }));
        return;
      }

      try {
        const response = await axios.get(
          `${API_ENDPOINT}/user/my-transactions-export-status/${jobId}`,
          getHeader()
        );
        const job = response.data;
        setExportJob(job);

        if (["done", "failed", "cancelled"].includes(job.status)) {
          stopPolling();

          if (job.status === "done" && job.fileUrl) {
            triggerDownload(job.fileUrl);
            setSnackbar({
              open: true,
              message: "Export ready — downloading now.",
            });
          }
        }
      } catch (err) {
        console.error("[export] Poll error:", err);
      }
    },
    [stopPolling]
  );

  const startPolling = useCallback(
    (jobId) => {
      stopPolling();
      pollStartRef.current = Date.now();
      pollJobStatus(jobId);
      pollTimerRef.current = setInterval(
        () => pollJobStatus(jobId),
        POLL_INTERVAL_MS
      );
    },
    [pollJobStatus, stopPolling]
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  const triggerDownload = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = "transaction_report.csv"; // <-- Changed to .csv
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = async () => {
    if (exportJob?.status === "done" && exportJob?.fileUrl) {
      triggerDownload(exportJob.fileUrl);
      return;
    }

    if (exportJob?.status === "pending" || exportJob?.status === "processing") {
      setSnackbar({
        open: true,
        message: "Your export is already in progress.",
      });
      return;
    }

    try {
      setRequesting(true);
      const response = await axios.post(
        `${API_ENDPOINT}/user/my-transactions-export-request?type=${usageType}`,
        {},
        getHeader()
      );

      const job = response.data;
      setExportJob(job);

      if (job.status === "done" && job.fileUrl) {
        triggerDownload(job.fileUrl);
      } else {
        startPolling(job.jobId);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setRequesting(false);
    }
  };

  const handleDismissBanner = async () => {
    stopPolling();
    if (exportJob?.jobId && exportJob?.status === "pending") {
      try {
        await axios.delete(
          `${API_ENDPOINT}/user/my-transactions-export-cancel/${exportJob.jobId}`,
          getHeader()
        );
      } catch (_) {}
    }
    setExportJob(null);
  };

  const handlePageChange = (_, newPage) => setPage(newPage);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "PPpp");
    } catch {
      return "Invalid Date";
    }
  };

  const exportInProgress =
    exportJob?.status === "pending" || exportJob?.status === "processing";

  const exportButtonLabel = (() => {
    if (exportInProgress) return "Processing…";
    if (exportJob?.status === "done") return "Download Again";
    return "Export";
  })();

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;

  return (
    <Container sx={{ mt: 3 }}>
      <TitleActionsContainer>
        <PageTitle>My Usage History</PageTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", sm: "auto" }, mt: { xs: 1, sm: 0 } }}
        >
          <FormControl size="small" sx={{ width: 240 }} fullWidth>
            <InputLabel id="usage-type-label">Usage Type</InputLabel>
            <Select
              labelId="usage-type-label"
              id="usage-type-select"
              value={usageType}
              label="Usage Type"
              onChange={(e) => {
                setUsageType(e.target.value);
                setPage(1);
                stopPolling();
                setExportJob(null);
              }}
            >
              <MenuItem value="">
                <i>None</i>
              </MenuItem>
              <MenuItem value="usage">Usage</MenuItem>
              <MenuItem value="recharge">Recharge</MenuItem>
            </Select>
          </FormControl>

          <Tooltip
            title={
              exportInProgress
                ? "Export is being processed in the background"
                : exportJob?.status === "done"
                ? "Click to download your report again"
                : "Export all transactions to CSV"
            }
          >
            <span>
              <LoadingButton
                sx={{ width: 240 }}
                startIcon={
                  exportJob?.status === "done" ? (
                    <DownloadDoneIcon />
                  ) : (
                    <ImportExportIcon />
                  )
                }
                variant="contained"
                color={exportJob?.status === "done" ? "success" : "primary"}
                loading={requesting || exportInProgress}
                onClick={handleExport}
              >
                {exportButtonLabel}
              </LoadingButton>
            </span>
          </Tooltip>
        </Stack>
      </TitleActionsContainer>

      <ExportStatusBanner
        jobState={exportJob}
        onDismiss={handleDismissBanner}
        onDownload={triggerDownload}
      />

      <TableContainer
        sx={{ mt: 1 }}
        component={Paper}
        variant="outlined"
        elevation={3}
      >
        <Table sx={{ minWidth: 650 }} aria-label="transaction history">
          <TableHead>
            <TableRow>
              <TableCell>SI.no.</TableCell>
              <TableCell>Message Id</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.data.map((transaction, i) => (
              <TableRow
                key={transaction._id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell>{20 * (page - 1) + i + 1}</TableCell>
                <TableCell>{transaction._id}</TableCell>
                <TableCell>
                  <Chip
                    label={transaction.type}
                    color={transaction.type === "credit" ? "success" : "error"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography
                    color={
                      transaction.type === "credit"
                        ? "success.main"
                        : "error.main"
                    }
                    fontWeight="bold"
                  >
                    {transaction.type === "credit" ? "+" : "-"}
                    {transaction.amount}
                  </Typography>
                </TableCell>
                <TableCell>{transaction.category}</TableCell>
                <TableCell>{transaction?.recipient ?? "NA"}</TableCell>
                <TableCell>{transaction?.templateName ?? "NA"}</TableCell>
                <TableCell>{formatDate(transaction.updatedOn)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!data?.data?.length && (
        <Box m={4}>
          <Typography mt={4} textAlign="center" variant="h6" color="GrayText">
            No transactions found
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 2,
          alignItems: "center",
        }}
      >
        <Typography variant="caption">
          Total Items: {data.totalItems}
        </Typography>
        <Pagination
          count={data.totalPages}
          page={data.currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
};

export default TransactionHistory;
